import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PanZoom } from '../../../view/PanZoom';
import { AdaptiveBrushCursorAction } from '../../actions/contouring-actions/AdaptiveBrushCursorAction';
import { AdaptiveBrushDrawAction } from '../../actions/contouring-actions/AdaptiveBrushDrawAction';
import { BrushTool } from './BrushTool';
import { ReadonlyVec2 } from 'gl-matrix';
import { View } from '~/engine/view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';

/**
 * {@link AdaptiveBrushTool} is a {@link DrawingTool} that allows the user to draw on the {@link RegionOfInterest} using an adaptive brush.\
 * The brush is a circle with a diameter of {@link AdaptiveBrushTool.brushSize} millimeters.\
 * It adapts to an image's intensity values and is used to draw on the {@link RegionOfInterest} using the {@link AdaptiveBrushDrawAction}.
 *
 * {@link AdaptiveBrushTool} inherits from {@link DrawingTool} and thus provides {@link PanAction} and {@link ZoomAction} functionality.\
 * It also uses {@link AdaptiveBrushDrawAction} and {@link AdaptiveBrushCursorAction} to handle the drawing.
 */
export class AdaptiveBrushTool extends BrushTool {
    /**
     * Method to create a new {@link AdaptiveBrushTool} instance.\
     * The {@link AdaptiveBrushTool} is initialized with 4 actions;
     * {@link PanAction}, {@link ZoomAction}, {@link AdaptiveBrushCursorAction} and {@link AdaptiveBrushDrawAction}.
     *
     * @param panZoom Reference to {@link PanZoom}-object, which is used to provide default Panning and Zooming functionality.
     */
    public constructor(
        private readonly image: ImageObject,
        protected readonly structure: RegionOfInterest,
        panZoom: PanZoom,
        erase: boolean,
        brushDiameter: number,
        brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super(
            panZoom,
            erase,
            brushDiameter,
            structure,
            brushSizeChangedCallback
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (buttons === 0) {
            if (this._drawState) {
                this._drawState.clearMemory();
                this._drawState = undefined;
            }
            if (
                view instanceof PlaneView &&
                view.label === Orientations.TRANSVERSAL_ORIENTATION &&
                this.structure.dicomParent.approvalStatus !==
                    DICOMApprovalStatus.APPROVED
            ) {
                if (!this._cursorAction) {
                    this.resetCursorAction();
                }
                return (
                    this._cursorAction?.onMouseMove(
                        coordinates,
                        buttons,
                        view
                    ) ??
                    new EventHandlerResult(
                        EventHandlerResultType.EVENT_NOT_HANDLED
                    )
                );
            }
        }
        if (buttons === 1) {
            if (this._drawState) {
                return this._drawState.onMouseMove(coordinates, buttons, view);
            }
        }
        if (buttons === 4) {
            return this._panAction.onMouseMove(coordinates, buttons, view);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Method to create a {@link StaticBrushCursorAction} instance, which is the type of {@link CursorAction} used by {@link StaticBrushTool}.
     * @returns A new {@link StaticBrushCursorAction} instance, with diameter set to {@link StaticBrushTool.diameter}.
     */
    protected resetCursorAction(): void {
        this._cursorAction = new AdaptiveBrushCursorAction(
            this.image,
            this._diameter,
            this.structure.color
        );
    }

    protected resetDrawAction(): void {
        this._drawAction = new AdaptiveBrushDrawAction(
            this.structure,
            this.image,
            this._diameter,
            this._erase,
            0,
            1
        );
    }
}
